import { createSlice } from '@reduxjs/toolkit';
import { processRequest, RequestStatus } from './network/managedRequest';
import { API_SHORT } from '../constants';
import { PbDispatch, PbGetState } from './index';
import { PbRequest } from './pbRequest';
import { pbRequestReset } from './pbRequestSlice';

interface ApiSuccessDto {
    status: number;
    message: string;
    timestamp: number;
}

export interface ActivationCodeDto {
    code: string;
    email: string;
}

export interface RegisterPartnerDto {
    salutation: string;
    first_name: string;
    last_name: string;
    company_name: string;
    phone: string;
    email: string;
    email_confirm: string;
    terms_accepted: boolean;
    password: string;
    password_confirm: string;
    country_id: number;
    lang: string;
}

interface RegistrationStore {
    email?: string;
    password?: string;
}

export interface CallBackParams<T> {
    data: T;
    onSuccess?: () => void;
}

const initialState: RegistrationStore = {
    email: null,
    password: null,
};

const registrationSlice = createSlice({
    name: 'registration',
    initialState,
    reducers: {
        updatePartnerLogin(state, action) {
            state.email = action.payload.email;
            state.password = action.payload.password;
        },
    },
});

const { updatePartnerLogin } = registrationSlice.actions;

export function ActivateRegistration(
    params: CallBackParams<ActivationCodeDto>
) {
    return executeManagedRequest(
        '/v1/signup/activate',
        PbRequest.ACTIVATION_REQUEST,
        params,
        JSON.stringify({
            confirm_code: params.data.code,
            email: params.data.email,
        })
    );
}

export function RegisterPartnerManager(
    params: CallBackParams<RegisterPartnerDto>
) {
    return async (dispatch: PbDispatch, getState: PbGetState) => {
        await executeManagedRequest(
            '/v1/signup/partner_manager',
            PbRequest.REGISTRATION_REQUEST,
            params
        )(dispatch);
        if (
            getState().pbRequest[PbRequest.REGISTRATION_REQUEST].status ===
            RequestStatus.SUCCESS
        ) {
            dispatch(
                updatePartnerLogin({
                    email: params.data.email,
                    password: params.data.password,
                })
            );
        }
    };
}

export function ResendCode(params: CallBackParams<string>) {
    return executeManagedRequest(
        '/v1/signup/renew',
        PbRequest.RESEND_CODE_REQUEST,
        params,
        JSON.stringify({
            email: params.data,
        })
    );
}

function executeManagedRequest<T>(
    urlSuffix: string,
    requestType: PbRequest,
    params: CallBackParams<T>,
    overrideBody?: string
) {
    return async (dispatch: PbDispatch) => {
        try {
            const request = {
                method: 'post',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: overrideBody ?? JSON.stringify(params.data),
            };
            await processRequest<ApiSuccessDto>(
                dispatch,
                `${API_SHORT}` + urlSuffix,
                request,
                requestType
            );
            params?.onSuccess?.();
        } catch {}
    };
}

export function ResetRequestsToInitialStates() {
    return async (dispatch: PbDispatch) => {
        dispatch(pbRequestReset(PbRequest.REGISTRATION_REQUEST));
        dispatch(pbRequestReset(PbRequest.ACTIVATION_REQUEST));
        dispatch(pbRequestReset(PbRequest.RESEND_CODE_REQUEST));
    };
}

export default registrationSlice.reducer;
